import { computed, Injectable, signal } from '@angular/core';
import { CURRENCY, ISpot, IWallet, WALLET_CODE } from '@libs/interfaces';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { IToast, MODAL_TYPE } from '@libs/store/toast/model';
import { ApiService } from '../api.service';
import { StoreToastModalsService } from '../store/store-toast-modals.service';
import { StoreWalletService } from '../store/store-wallet.service';
import { Wallets } from '@libs/helper/wallet/wallets.helper';
@Injectable({
  providedIn: 'root',
})
export class WalletService {
  wallet = signal<IWallet>(null);
  currency = computed<CURRENCY>(() => this.wallet()?.currency);
  code = computed<WALLET_CODE>(() => this.wallet()?.code);
  wallets = signal<IWallet[]>(Wallets.filter((wallet: IWallet) => wallet.type === 'fiat'));
  spots = signal<ISpot[]>([]);
  constructor(
    private apiService: ApiService,
    private storeWalletService: StoreWalletService,
    private storeToastModalsService: StoreToastModalsService,
  ) {}

  /**
   * Sets the selected currency and updates the user wallet.
   * @param {WALLET_CODE} code - The wallet code.
   * @returns {Observable<any>} An observable that completes when the currency is set.
   */
  setSelectedCurrency(code: WALLET_CODE): Observable<any> {
    if (code == this.code()) return of(null);
    const currency = this.wallets().find((ds: IWallet) => ds.code == code);
    this.storeWalletService.setCurrency(currency);
    return this.updateUserWallet(code).pipe(tap(() => this.toastCurrency()));
  }

  /**
   * Updates the selected wallet based on the given currency.
   * @param {CURRENCY} currency - The currency.
   * @returns {Observable<void>} An observable that completes when the wallet is updated.
   */
  updateSelectedWallet(currency: CURRENCY) {
    const wallet = this.wallets().find((ds: IWallet) => ds.currency == currency);
    this.wallet.set(wallet);
    this.storeWalletService.setWallet(wallet);
  }

  /**
   * Updates the user wallet based on the given wallet code.
   * @param {WALLET_CODE} code - The wallet code.
   * @returns {Observable<any>} An observable that completes when the user wallet is updated.
   */
  private updateUserWallet(code: WALLET_CODE) {
    const wallet = this.wallets().find(w => w.code == code);
    return this.apiService.patch(`user/wallet/${code}`).pipe(
      tap(() => this.storeWalletService.updateUserCurrency(wallet)),
      tap(() => this.storeWalletService.updateUserWalletType(wallet.type)),
      tap(() => this.updateSelectedWallet(CURRENCY[code])),
    );
  }

  /**
   * Displays a toast message for currency change.
   */
  private toastCurrency() {
    const toast: IToast = {
      id: Date.now(),
      info: 'WALLET.CWE',
      type: MODAL_TYPE.WARNING,
      exp: 1000,
    };
    this.storeToastModalsService.addToast(toast);
  }
}
